import { CourierListItemDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Box, IconButton, Tooltip } from '@mui/material';
import { ActionDelete } from '../../../../ui/components/pure/icons/ActionDelete.icon';
import { Colors } from '../../../../ui/theme/colors';
import { PermissionBoundary } from '../../../../core/permissions/permission-boundary.component';
import { TextButton } from '../../../../ui/components/pure/text-button/text-button.component';
import { initTable } from '../../../../ui/components/pure/list-table/list-table.init';
import { pinned } from '../../../../ui/components/pure/list-table/list-table-column-styles';
import { TableComponents } from '../../../../ui/components/pure/list-table/list-table-cells.component';
import { withoutRowNavigation } from '../../logic/without-row-navigation';

const FIXED_COLUMN_WIDTHS = {
  mobile: '220px',
  created: '150px',
  action: '270px'
} as const;

function IncompleteCourierActionsCell({
  onCompleteRegistration,
  onDeactivate
}: {
  onCompleteRegistration(): void;
  onDeactivate(): void;
}) {
  return (
    <PermissionBoundary availableUiFeatures={['internal-groundfloor']}>
      <Box
        style={{ gap: '8px' }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end'
        }}
      >
        <TextButton color="primary" variant="outlined" onClick={withoutRowNavigation(onCompleteRegistration)}>
          Complete registration
        </TextButton>
        <Tooltip title="Deactivate">
          <IconButton size="small" aria-label="Deactivate" onClick={withoutRowNavigation(onDeactivate)}>
            <ActionDelete color={Colors.cancelledDeletedRed} />
          </IconButton>
        </Tooltip>
      </Box>
    </PermissionBoundary>
  );
}

interface IncompleteCourierSortFields {
  createdTime: string;
  groupName: string;
}

/** Mobile, phone group and created date: an incomplete courier has no name or carrier yet. */
export function useIncompleteCouriersTable(
  onCompleteRegistration: (row: CourierListItemDto) => void,
  onDeactivate: (row: CourierListItemDto) => void
) {
  const tb = initTable<CourierListItemDto, IncompleteCourierSortFields>();
  const rowActions = {
    onCompleteRegistration: (row: CourierListItemDto) => () => onCompleteRegistration(row),
    onDeactivate: (row: CourierListItemDto) => () => onDeactivate(row)
  };

  tb.styles({
    table: { tableLayout: 'fixed' }
  });

  tb.rowStyles({
    td: { height: '64px' }
  });

  tb.column('MOBILE', TableComponents.BoldText, {
    value: ':mobile'
  }).options({
    isSortable: false,
    minBreakpoint: 'md',
    tooltip: `courier mobile number`,
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.mobile)
  });

  // The phone group shows how far they got: mobile entered or verified.
  tb.column('GROUP', TableComponents.Text, {
    value: (row) => row.carrier?.name ?? ''
  }).options({
    isSortable: true,
    sortId: 'groupName',
    minBreakpoint: 'md',
    tooltip: `the phone group the courier stopped in`
  });

  tb.column('CREATED', TableComponents.DateOnly, {
    value: ':createdTime'
  }).options({
    isSortable: true,
    sortId: 'createdTime',
    minBreakpoint: 'md',
    tooltip: `courier created date`,
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.created)
  });

  tb.column('', IncompleteCourierActionsCell, rowActions).options({
    isSortable: false,
    minBreakpoint: 'md',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.action)
  });

  // --- Mobile Columns ---
  tb.columnGroup('MOBILE')
    .row(TableComponents.BoldText, { value: ':mobile' })
    .row(TableComponents.Text, { value: (row) => row.carrier?.name ?? '' })
    .options({
      isSortable: false,
      maxBreakpoint: 'sm',
      tooltip: 'courier mobile number'
    });

  tb.columnGroup('')
    .row(TableComponents.DateOnly, { value: ':createdTime' })
    .row(IncompleteCourierActionsCell, rowActions)
    .options({
      isSortable: true,
      sortId: 'createdTime',
      maxBreakpoint: 'sm',
      styles: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }
    });

  return tb;
}
